import {
  listingFields,
  PROVINCIA_OPTIONS,
  MODALIDAD_OPTIONS,
  TIPO_OPERACION_OPTIONS,
  FACTURACION_SCALE,
  EMPLEADOS_SCALE,
  ANTIGUEDAD_SCALE,
} from '../../config/configListing';
import { VALORADOR_RANGES, migrateValoradorAnswers } from '../../util/valoradorListing';
import {
  VALORADOR_FINANCIAL_BANDS,
  UNKNOWN_ANSWER,
  NOT_APPLICABLE_ANSWER,
  migrateValoradorFinancialAnswers,
} from '../../util/valoradorFinancialBands';
import {
  financialProfileOptions,
  resolveFinancialProfile,
} from '../../util/valoradorEngine/profiles';
import options from './prototypeOptions.json';

export const sectorOptions = listingFields.find(field => field.key === 'sector').enumOptions;
export const sectors = sectorOptions.flatMap(sector =>
  sector.suboptions?.length ? sector.suboptions : [sector]
);
export const buyerSectors = sectorOptions.flatMap(sector => [sector, ...(sector.suboptions || [])]);
const choice = (name, label, list, multi = false) => ({
  name,
  label,
  type: multi ? 'multi' : 'choice',
  options: list,
});
const financial = name => ({
  ...single(choice(name, name, VALORADOR_FINANCIAL_BANDS[name])),
  help: `${name}BandHelp`,
});
const uncertain = list => [...list, UNKNOWN_ANSWER];
const single = field => ({
  id: field.name,
  title: field.label,
  fields: [field],
});
const yesNo = [
  { option: 'si', messageKey: 'yes' },
  { option: 'no', messageKey: 'no' },
];

/** Short screens share the listing's options; only relevant follow-up screens appear. */
export const getValoradorSteps = answers => {
  const profile = resolveFinancialProfile(answers);
  const profileOptions = financialProfileOptions(answers.sector);
  return [
    {
      ...single(
        choice('mode', 'intentTitle', [
          { option: 'seller', messageKey: 'seller', helpKey: 'sellerHelp' },
          { option: 'buyer', messageKey: 'buyer', helpKey: 'buyerHelp' },
        ])
      ),
      help: 'intentHelp',
    },
    single(choice('sector', 'sectorQuestion', sectors)),
    ...(profileOptions.length > 1
      ? [
          {
            ...single(
              choice('financialProfile', 'financialProfileQuestion', [
                ...profileOptions,
                { option: 'notCovered', messageKey: 'profileNotCovered' },
              ])
            ),
            help: 'financialProfileHelp',
          },
        ]
      : []),
    single(choice('province', 'provinceQuestion', PROVINCIA_OPTIONS)),
    single(choice('modality', 'modalityQuestion', MODALIDAD_OPTIONS)),
    single(
      choice(
        'empleados',
        'teamQuestion',
        EMPLEADOS_SCALE.map(row => ({
          option: String(row.lowerBound),
          label: row.label,
        }))
      )
    ),
    single(choice('antiguedad', 'ageQuestion', ANTIGUEDAD_SCALE)),
    ...(answers.mode === 'seller'
      ? [
          {
            ...single(choice('operations', 'saleOps', TIPO_OPERACION_OPTIONS, true)),
            help: 'multiHelp',
          },
        ]
      : []),
    single(choice('facturacion', 'turnoverQuestion', FACTURACION_SCALE)),
    financial('profit'),
    single(
      choice('g12', 'g12', [
        ...uncertain(options.GROWTH),
        { ...NOT_APPLICABLE_ANSWER, messageKey: 'noComparison' },
      ])
    ),
    single(
      choice('g24', 'g24', [
        ...uncertain(options.GROWTH),
        { ...NOT_APPLICABLE_ANSWER, messageKey: 'noComparison' },
      ])
    ),
    ...(profile?.vis === 'recurrencia'
      ? [single(choice('rec', 'rec', uncertain(options.REC)))]
      : []),
    ...(profile?.vis === 'recurrencia' && ['r2', 'r3', 'r4', 'r5'].includes(answers.rec)
      ? [single(choice('model', 'model', uncertain(options.MODELO)))]
      : []),
    ...(profile?.vis === 'backlog'
      ? [
          single(
            choice('backlog', 'backlog', [...uncertain(options.BACKLOG), NOT_APPLICABLE_ANSWER])
          ),
        ]
      : []),
    ...(profile?.conc
      ? [single(choice('concentration', 'concentration', uncertain(options.CONC)))]
      : []),
    ...['salary', 'personal', 'extraIncome', 'extraExpense'].map(financial),
    single(
      choice('rentPaid', 'rentPaid', [
        ...yesNo,
        { option: 'none', messageKey: 'noPremises' },
        UNKNOWN_ANSWER,
      ])
    ),
    ...(answers.rentPaid === 'no' ? [financial('rent')] : []),
    financial('debt'),
    financial('cash'),
    single(choice('propertyIncluded', 'propertyIncluded', uncertain(yesNo))),
    ...(answers.propertyIncluded === 'si' ? [financial('property')] : []),
    ...(profile?.stock ? [financial('stock')] : []),
    ...(answers.mode === 'buyer'
      ? [
          {
            ...single(choice('targetSector', 'targetSector', buyerSectors)),
            help: 'criteriaHelp',
          },
          {
            ...single(
              choice(
                'targetProvinces',
                'targetProvinces',
                [{ option: 'toda-espana', messageKey: 'spain' }, ...PROVINCIA_OPTIONS],
                true
              )
            ),
            help: 'provinceSelectionHelp',
          },
          {
            ...single(
              choice(
                'targetOperations',
                'targetOperations',
                TIPO_OPERACION_OPTIONS.map((row, index) => ({
                  ...row,
                  messageKey: ['buyFull', 'buyMinor', 'buyMajor'][index],
                })),
                true
              )
            ),
            help: 'multiHelp',
          },
          {
            ...single(choice('targetModalities', 'targetModalities', MODALIDAD_OPTIONS, true)),
            help: 'multiHelp',
          },
          ...VALORADOR_RANGES.map(({ name, scale }) => ({
            id: name,
            title: name,
            help: 'rangeHelp',
            fields: [{ name, label: name, type: 'range', scale }],
          })),
        ]
      : []),
  ];
};

/** Remove conditional answers when their controlling choice changes. */
export const cleanValoradorAnswers = (values, previous = {}) => {
  const next = { ...values };
  if (previous.sector && previous.sector !== next.sector) delete next.financialProfile;
  const profile = resolveFinancialProfile(next);
  if (profile?.vis !== 'recurrencia') {
    delete next.rec;
    delete next.model;
  }
  if (profile?.vis !== 'backlog') delete next.backlog;
  if (!profile?.conc) delete next.concentration;
  if (!profile?.stock) delete next.stock;
  if (next.rentPaid !== 'no') delete next.rent;
  if (next.propertyIncluded !== 'si') delete next.property;
  if (!['r2', 'r3', 'r4', 'r5'].includes(next.rec)) delete next.model;
  if (previous.mode && previous.mode !== next.mode) {
    Object.keys(next)
      .filter(key => key.startsWith('target') || key === 'operations')
      .forEach(key => delete next[key]);
  }
  return next;
};

/** Retain saved answers and the current question when splitting the old paired screens. */
export const restoreValoradorDraft = draft => {
  const answers = migrateValoradorFinancialAnswers(migrateValoradorAnswers(draft.answers));
  const steps = getValoradorSteps(answers);
  if (draft.version === 1) return { ...draft, answers, step: 0, screen: 'questions' };
  if (draft.version === 4)
    return {
      ...draft,
      answers,
      step: Math.max(
        0,
        steps.findIndex(row => row.id === draft.stepId)
      ),
    };
  // Prior versions asked generic quality questions and stock for every sector.
  const oldIds = [
    'mode',
    'sector',
    'province',
    'modality',
    'empleados',
    'antiguedad',
    ...(answers.mode === 'seller' ? ['operations'] : []),
    'facturacion',
    'profit',
    'g12',
    'g24',
    'rec',
    ...(['r2', 'r3', 'r4', 'r5'].includes(answers.rec) ? ['model'] : []),
    'backlog',
    'concentration',
    ...(draft.version === 2
      ? ['ownerExpenses', 'extraordinary']
      : ['salary', 'personal', 'extraIncome', 'extraExpense']),
    'rentPaid',
    ...(answers.rentPaid === 'no' ? ['rent'] : []),
    ...(draft.version === 2 ? ['balance'] : ['debt', 'cash']),
    'propertyIncluded',
    ...(answers.propertyIncluded === 'si' ? ['property'] : []),
    'stock',
    ...(answers.mode === 'buyer'
      ? [
          'targetSector',
          'targetProvinces',
          'targetOperations',
          'targetModalities',
          ...VALORADOR_RANGES.map(row => row.name),
        ]
      : []),
  ];
  const firstQuestion = {
    ownerExpenses: 'salary',
    extraordinary: 'extraIncome',
    balance: 'debt',
  };
  const nextId = oldIds
    .slice(draft.step)
    .map(id => firstQuestion[id] || id)
    .find(id => steps.some(step => step.id === id));
  const step = steps.findIndex(row => row.id === nextId);
  return { ...draft, answers, step: Math.max(0, step) };
};

/** Used when revising a result: retain valid answers and ask only for missing follow-ups. */
export const isValoradorStepComplete = (step, values) =>
  step.fields.every(field => {
    if (field.type === 'range') {
      const min = values[field.name + 'Min'];
      const max = values[field.name + 'Max'];
      return (
        min !== '' &&
        max !== '' &&
        field.scale.some(row => row.idx === +min) &&
        field.scale.some(row => row.idx === +max) &&
        +min <= +max
      );
    }
    const selected = field.type === 'multi' ? values[field.name] : [values[field.name]];
    return (
      Array.isArray(selected) &&
      selected.length > 0 &&
      selected.every(value => field.options.some(row => row.option === value)) &&
      (field.name !== 'targetProvinces' || selected.length <= 3)
    );
  });
